<!-- app/javascript/dashboard/routes/dashboard/kanban/components/KanbanCard.vue -->
<template>
  <div
    class="bg-amber-50 rounded-md p-3 card-shadow cursor-pointer"
    @click="handleClick"
    @mousedown.prevent
    @keydown.enter.prevent="handleClick"
    @keydown.space.prevent="handleClick"
    role="button"
    tabindex="0"
  >
    <!-- avatar + nome + inbox -->
    <div class="flex items-center gap-2">
      <img
        :src="avatarSrc(card)"
        alt="avatar"
        class="kanban-avatar flex-none"
        referrerpolicy="no-referrer"
        loading="lazy"
        @error="onAvatarError"
      />
      <div class="min-w-0">
        <div class="font-medium truncate">
          {{ card.contact_name || card.title || 'Contato' }}
        </div>
        <div
          v-if="card.inbox_description || card.inbox_name"
          class="text-xs text-gray-500 truncate"
        >
          {{ card.inbox_description || card.inbox_name }}
        </div>
      </div>
    </div>

    <!-- última mensagem (imagem / áudio / arquivo / texto) -->
    <div v-if="(card.last_messages || []).length" class="mt-2 space-y-2">
      <div
        v-for="(m, i) in card.last_messages"
        :key="i"
        class="msg-row"
        :class="m.outgoing ? 'msg-row--right' : 'msg-row--left'"
      >
        <div
          class="msg-bubble"
          :class="[
            m.outgoing ? 'msg-bubble--right' : 'msg-bubble--left',
            {
              'msg-bubble--out-audio': m.outgoing && m.audio_url,
              'msg-bubble--full': !!m.audio_url
            }
          ]"
        >
          <!-- 1) IMAGEM -->
          <div v-if="m.has_image" class="msg-image-wrap mt-2" @click.stop>
            <button
              type="button"
              class="msg-image-button"
              title="Abrir imagem"
              @click.stop.prevent="openImagePreviewFn(m.image_url)"
            >
              <img
                :src="m.image_thumb_url || m.image_url"
                alt="imagem da conversa"
                class="msg-image-thumb"
                loading="lazy"
                referrerpolicy="no-referrer"
              />
            </button>
          </div>

          <!-- 2) ÁUDIO -->
          <div v-else-if="m.audio_url" class="msg-audio-wrap mt-2" @click.stop>
            <audio
              class="msg-audio w-full"
              style="width:100%;max-width:100%;display:block"
              :src="m.audio_url"
              controls
              preload="none"
            ></audio>
          </div>

          <!-- 3) ARQUIVO -->
          <div
            v-else-if="m.has_file || m.file_url || m.has_pdf || m.pdf_url || fileAttachment(m).exists"
            class="text-sm mt-2"
            @click.stop
          >
            <div class="truncate">
              {{ explicitFileName(m) || fileAttachment(m).name || 'arquivo' }}
            </div>
            <div class="truncate">
              <strong>{{ explicitFileName(m) || fileAttachment(m).name || 'arquivo' }}</strong>
            </div>
            <div>
              <a
                :href="explicitFileUrl(m) || fileAttachment(m).url"
                :download="explicitFileName(m) || fileAttachment(m).name || 'arquivo'"
                target="_blank"
                rel="noopener noreferrer"
                class="dl-link"
                @click.stop
                @mousedown.stop
                @mouseup.stop
                @touchstart.stop
                @touchend.stop
              >Baixar</a>
            </div>
          </div>

          <!-- 4) TEXTO -->
          <div v-else-if="m.content" class="text-sm break-words whitespace-pre-line mt-2">
            {{ m.content }}
          </div>

          <!-- META -->
          <div class="msg-meta" :class="m.outgoing ? 'msg-meta--right' : 'msg-meta--left'">
            <span class="msg-time-badge">{{ formatMsgTimeFn(m.created_at) }}</span>

            <!-- checks (apenas enviadas) -->
            <span v-if="m.outgoing && m.checks_state === 'double_blue'" class="msg-checks-wrap">
              <svg class="msg-checks msg-checks--double msg-checks--double-blue" viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet">
                <path d="M5 13l3 3l6-6" vector-effect="non-scaling-stroke" />
                <path d="M10 13l3 3l6-6" vector-effect="non-scaling-stroke" />
              </svg>
            </span>
            <span v-else-if="m.outgoing && m.checks_state === 'double_gray'" class="msg-checks-wrap">
              <svg class="msg-checks msg-checks--double msg-checks--double-gray" viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet">
                <path d="M5 13l3 3l6-6" vector-effect="non-scaling-stroke" />
                <path d="M10 13l3 3l6-6" vector-effect="non-scaling-stroke" />
              </svg>
            </span>
            <span v-else-if="m.outgoing && m.checks_state === 'single'" class="msg-checks-wrap">
              <svg class="msg-checks msg-checks--single" viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet">
                <path d="M6 13l3 3l9-9" vector-effect="non-scaling-stroke" />
              </svg>
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-2 text-[11px] text-gray-400">#{{ card.id }}</div>
  </div>
</template>

<script>
export default {
  name: 'KanbanCard',
  props: {
    card: { type: Object, required: true },
    col: { type: Object, required: true },
    index: { type: Number, required: true },
    isDropAtFn: { type: Function, required: true },
    onCardClickFn: { type: Function, required: true },
    formatMsgTimeFn: { type: Function, required: true },
    openImagePreviewFn: { type: Function, required: true },
  },

  methods: {
    handleClick(e) {

      this.onCardClickFn(this.card, e);
    },

    /* ===== avatar fallback ===== */
    defaultAvatarDataUri() {
      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128">
          <circle cx="64" cy="64" r="64" fill="#d1d5db"/>
          <circle cx="64" cy="52" r="28" fill="#f3f4f6"/>
          <path d="M24 112a40 40 0 0 1 80 0" fill="#f3f4f6"/>
        </svg>
      `;
      return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg.trim());
    },
    avatarSrc(card) {
      const u = (card?.avatar_url || '').trim();
      return u ? u : this.defaultAvatarDataUri();
    },
    onAvatarError(e) {
      try { e.target.onerror = null; } catch {}
      e.target.src = this.defaultAvatarDataUri();
    },

    /* ===== helpers ===== */
    filenameFromUrl(url) {
      try {
        const u = new URL(url);
        const last = u.pathname.split('/').pop() || '';
        return decodeURIComponent(last) || '';
      } catch (e) {
        const last = (url || '').split('/').pop() || '';
        try { return decodeURIComponent(last); } catch { return last || ''; }
      }
    },
    extFromContentType(ct) {
      if (typeof ct !== 'string') return '';
      const map = {
        'application/pdf': 'pdf',
        'application/msword': 'doc',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
        'application/vnd.ms-excel': 'xls',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
        'application/zip': 'zip',
        'application/x-7z-compressed': '7z',
        'text/plain': 'txt',
        'application/json': 'json',
        'application/vnd.ms-powerpoint': 'ppt',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
      };
      return map[ct] || '';
    },
    isImageMime(ct) { return typeof ct === 'string' && ct.startsWith('image/'); },
    isAudioMime(ct) { return typeof ct === 'string' && ct.startsWith('audio/'); },
    urlLooksLikeImage(url) { return /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i.test((url || '').toLowerCase()); },
    urlLooksLikeAudio(url) { return /\.(mp3|ogg|wav|m4a|aac)(\?.*)?$/i.test((url || '').toLowerCase()); },
    urlLooksLikeMedia(url) { return this.urlLooksLikeImage(url) || this.urlLooksLikeAudio(url); },

    pickUrl(obj) {
      return (
        obj?.download_url ||
        obj?.data_url ||
        obj?.external_url ||
        obj?.url ||
        obj?.file_url ||
        obj?.pdf_url ||
        obj?.attachment_url ||
        ''
      );
    },
    collectAttachments(obj) {
      const out = [];
      if (Array.isArray(obj?.attachments)) out.push(...obj.attachments);
      if (Array.isArray(obj?.content_attributes?.attachments)) out.push(...obj.content_attributes.attachments);
      if (obj?.content_attributes?.attachment) out.push(obj.content_attributes.attachment);
      if (Array.isArray(obj?.attachments_payload)) out.push(...obj.attachments_payload);
      return out;
    },
    extractFilenameFromText(text) {
      const t = (text || '').trim();
      if (!t) return '';
      const m = t.match(/([^\s'"<>]+?\.(pdf|docx?|xlsx?|pptx?|csv|txt|zip|7z|json))/i);
      return m ? m[1] : '';
    },

    /* API explícita vinda do backend */
    explicitFileUrl(m) {
      return m?.file_url || m?.pdf_url || null;
    },
    explicitFileName(m) {
      const url = this.explicitFileUrl(m);
      return m?.file_name || m?.pdf_name || (url ? this.filenameFromUrl(url) : '');
    },

    /* Normaliza "arquivo" */
    getFileFrom(obj) {
      if (!obj || typeof obj !== 'object') return { exists: false, url: '', name: '' };
      if (obj.has_image || obj.image_url || obj.audio_url) return { exists: false, url: '', name: '' };

      const atts = this.collectAttachments(obj).filter(Boolean);
      if (atts.length) {
        const first = atts.find(a => {
          const ct = a?.content_type || '';
          const url = this.pickUrl(a);
          if (!url) return false;
          if (this.isImageMime(ct) || this.isAudioMime(ct)) return false;
          if (this.urlLooksLikeMedia(url)) return false;
          return true;
        }) || atts.find(a => {
          const url = this.pickUrl(a);
          return !!url && !this.urlLooksLikeMedia(url);
        });

        if (first) {
          const url = this.pickUrl(first);
          if (url && !this.urlLooksLikeMedia(url)) {
            let name =
              first.file_name ||
              first.filename ||
              first.name ||
              this.filenameFromUrl(url) ||
              this.extractFilenameFromText(obj.content);

            if (!name) {
              const ext = this.extFromContentType(first.content_type);
              name = ext ? `arquivo.${ext}` : 'arquivo';
            }
            if (this.isImageMime(first?.content_type) || this.isAudioMime(first?.content_type)) {
              return { exists: false, url: '', name: '' };
            }
            return { exists: true, url, name };
          }
        }
      }

      const url = this.pickUrl(obj);
      if (url && !this.urlLooksLikeMedia(url) && !obj.audio_url && !obj.has_image && !obj.image_url) {
        let name =
          obj.file_name ||
          obj.pdf_name ||
          obj.attachment_name ||
          this.filenameFromUrl(url) ||
          this.extractFilenameFromText(obj.content);
        if (!name) {
          const ext = this.extFromContentType(obj.content_type);
          name = ext ? `arquivo.${ext}` : 'arquivo';
        }
        return { exists: true, url, name };
      }

      return { exists: false, url: '', name: '' };
    },

    /* Retorna o anexo de ARQUIVO da mensagem; se não houver, fallback para o card */
    fileAttachment(m) {
      let cand = this.getFileFrom(m);
      if (cand.exists) return cand;
      cand = this.getFileFrom(this.card);
      return cand.exists ? cand : { exists: false, url: '', name: '' };
    },
  },
};
</script>

<style scoped>
/* Garante que o balão de áudio ocupe 100% da largura disponível do card */
.msg-bubble--full {
  width: 100% !important;
  max-width: 100% !important;
  display: block;
}
</style>